// src/pages/ProductDetailsPage.js
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import { flyImageToCart } from "../utils/flyToCart";
import ProductCard from "../components/ProductCard";
import {
  getAllProducts,
  getProductBySlug,
  getProductReviewsBySlug,
} from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { PRODUCT_CATEGORIES, PRODUCT_TAGS } from "../data/products";
import { useSeo } from "../hooks/useSeo";
import { FaAlignLeft, FaCubes, FaRulerVertical, FaTruck } from "react-icons/fa";
import { apiRequest } from "../services/api";

function ProductDetailsPage() {
  
  const { lang } = useContext(LanguageContext);
    const [principalCategory, setPrincipalCategory] = useState(null);
    const [outfitProducts, setOutfitProducts] = useState([]);
    const [relatedProducts, setRelatedProducts] = useState([]);
    
    const [searchParams, setSearchParams] = useSearchParams();
    // ✅ Sticky WhatsApp CTA (reappears after refresh)
  const [showWhatsappCTA, setShowWhatsappCTA] = useState(false);
  // ✅ Scroll-to-top button visibility
const [showScrollTop, setShowScrollTop] = useState(false);

  const [shipping, setShipping] = useState({
  fee: 4,
  free_enabled: false,
  free_threshold: 0,
});

  const { addToCart, toggleWishlist, isInWishlist } = useCart();
  const navigate = useNavigate();
  const { slug } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
const [reviewsData, setReviewsData] = useState({
  totalReviews: 0,
  averageRating: 0,
  isFallback: false,
  reviews: [],
});
const [reviewsLoading, setReviewsLoading] = useState(false);
const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // ✅ separate color selection from size selection
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  const [openDesc, setOpenDesc] = useState(false);
  const [openMaterial, setOpenMaterial] = useState(false);
  const [openHeel, setOpenHeel] = useState(false);
  const [openShip, setOpenShip] = useState(false);


  const [isPlacing, setIsPlacing] = useState(false);
  const [error, setError] = useState("");

  const [viewerOpen, setViewerOpen] = useState(false);

const productKey = product?.id || product?.seo_slug || null;

  const mainImageRef = useRef(null);

 const normalizeColor = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[-_]/g, "");

const setColorInUrl = (colorName) => {
  const params = new URLSearchParams(searchParams);

  params.set("color", normalizeColor(colorName));

  setSearchParams(params, { replace: true });
};


const colorFromUrl = searchParams.get("color");

const normalizedColorFromUrl = colorFromUrl
  ? normalizeColor(colorFromUrl)
  : null;


  // ✅ Delay WhatsApp CTA for better UX
useEffect(() => {
  const t = setTimeout(() => setShowWhatsappCTA(true), 1000); // 4s delay
  return () => clearTimeout(t);
}, []);
 
    // ✅ Show scroll-to-top button after scrolling
useEffect(() => {
  let ticking = false;

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        setShowScrollTop(window.scrollY > 300);
        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener("scroll", onScroll);
  return () => window.removeEventListener("scroll", onScroll);
}, []);

  useEffect(() => {
  apiRequest("/shipping-settings")
    .then((s) =>
      setShipping({
        fee: Number(s.default_fee ?? 4),
        free_enabled: !!s.free_enabled,
        free_threshold: Number(s.free_threshold ?? 0),
      })
    )
    .catch(() => {});
}, []);


  /* ---------------- FETCH PRODUCT ---------------- */
  useEffect(() => {
    let mounted = true;

    setLoading(true);
setProduct(null);
setActiveIndex(0);
setSelectedColor(null);
setSelectedVariant(null);
setError("");

    getProductBySlug(slug)
      .then((data) => {
        if (!mounted) return;

        setProduct(data);
        setActiveIndex(0);

        // ✅ auto-select first available color (so sizes show immediately)
        const firstVariant =
               (data.variants || []).find((v) => Number(v.stock_qty) > 0) || null;
               if (firstVariant && !normalizedColorFromUrl) {
  setSelectedColor(firstVariant.color_name);
  setSelectedVariant(null);
}
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    return () => {
      mounted = false;
    };
  }, [slug]);

    const getTagsForDetailsProduct = (p) => {
    const tags = [];
    if (p?.is_new_in) tags.push(PRODUCT_TAGS.NEW_IN);
    if (p?.is_bestseller) tags.push(PRODUCT_TAGS.BESTSELLER);
    if (Number(p?.sale_percent) > 0) tags.push(PRODUCT_TAGS.SALE);
    return tags;
  };

  const getTagsForCardProduct = (p) => {
    const tags = Array.isArray(p?.tags) ? [...p.tags] : [];
    if (Number(p?.discount) > 0 && !tags.includes(PRODUCT_TAGS.SALE)) {
      tags.push(PRODUCT_TAGS.SALE);
    }
    return tags;
  };

  // ✅ only variants that can actually be bought
const availableVariants = useMemo(() => {
  return (product?.variants || []).filter(
    (v) => Number(v.stock_qty) > 0
  );
}, [productKey]);

const productTags = useMemo(() => {
  if (!product) return [];
  const tags = [];
  if (product.is_new_in) tags.push(PRODUCT_TAGS.NEW_IN);
  if (product.is_bestseller) tags.push(PRODUCT_TAGS.BESTSELLER);
  if (Number(product.sale_percent) > 0) tags.push(PRODUCT_TAGS.SALE);
  return tags;
}, [productKey]);


  /* ---------------- DERIVED DATA (SAFE HOOKS) ---------------- */

 const colors = useMemo(() => {
  if (!availableVariants.length) return [];

  const map = new Map();

  availableVariants.forEach((v) => {
    if (!map.has(v.color_name)) {
      map.set(v.color_name, v.color_hex);
    }
  });

  return Array.from(map.entries()).map(([name, code]) => ({
    name,
    code,
  }));
}, [availableVariants.length]);


  // ✅ sizes depend on selectedColor, not selectedVariant
 const sizesByColor = useMemo(() => {
  if (!selectedColor) return [];

  return availableVariants.filter(
    (v) => v.color_name === selectedColor
  );
}, [availableVariants.length, selectedColor]);


    useEffect(() => {
    let mounted = true;

    if (!product?.seo_slug) return;

    // Get the product category from public list endpoint (because details endpoint doesn't include category)
    getAllProducts({ search: product.seo_slug })
      .then((rows) => {
        if (!mounted) return;

        const found = (rows || []).find((r) => r.seo_slug === product.seo_slug);
        setPrincipalCategory(found?.primary_category || null);
      })
      .catch(console.error);

    return () => {
      mounted = false;
    };
  }, [product?.seo_slug]);

    

useEffect(() => {
  let mounted = true;

  if (!slug) return;

  setReviewsLoading(true);
setReviewsExpanded(false);

  getProductReviewsBySlug(slug)
    .then((data) => {
      if (!mounted) return;

      setReviewsData({
  totalReviews: Number(data.totalReviews || 0),
  averageRating: Number(data.averageRating || 0),
  isFallback: !!data.isFallback,
  reviews: Array.isArray(data.reviews) ? data.reviews : [],
});
    })
    .catch(() => {
      if (!mounted) return;

      setReviewsData({
  totalReviews: 0,
  averageRating: 0,
  isFallback: false,
  reviews: [],
});
    })
    .finally(() => {
      if (mounted) setReviewsLoading(false);
    });

  return () => {
    mounted = false;
  };
}, [slug]);

 useEffect(() => {
  let mounted = true;

  if (!product || !principalCategory) return;

  getAllProducts({})
    .then((rows) => {
      if (!mounted) return;

      const mapped = (rows || []).map(mapBackendProduct)
        .filter((p) => p.id !== product.id);

      /* ---------- OUTFIT PRODUCTS ---------- */
      const isBag = principalCategory === PRODUCT_CATEGORIES.ACCESSORIES;

      const shoeCategories = [
        PRODUCT_CATEGORIES.HEELS,
        PRODUCT_CATEGORIES.SNEAKERS,
        PRODUCT_CATEGORIES.BOOTS,
        PRODUCT_CATEGORIES.FLATS_LOAFERS,
        PRODUCT_CATEGORIES.SANDALS,
      ];

      let outfit = mapped.filter((p) =>
        isBag
          ? shoeCategories.includes(p.category)
          : p.category === PRODUCT_CATEGORIES.ACCESSORIES
      );

      outfit = outfit
        .map((p) => {
          const shared = (p.tags || []).filter((t) =>
            productTags.includes(t)
          ).length;
          return { ...p, __score: shared };
        })
        .filter((p) => p.__score > 0)
        .sort((a, b) => b.__score - a.__score)
        .slice(0, 5);

      setOutfitProducts(outfit);

      /* ---------- RELATED PRODUCTS ---------- */
      let related = mapped
        .map((p) => {
          let score = 0;
          if (p.category === principalCategory) score += 2;
          score += (p.tags || []).filter((t) =>
            productTags.includes(t)
          ).length;
          return { ...p, __score: score };
        })
        .filter((p) => p.__score > 0)
        .sort((a, b) => b.__score - a.__score)
        .slice(0, 5);

      setRelatedProducts(related);
    })
    .catch(console.error);

  return () => {
    mounted = false;
  };
}, [productKey, principalCategory, productTags]);



    const totalStock = useMemo(() => {
    const arr = product?.variants || [];
    return arr.reduce((sum, v) => sum + (Number(v.stock_qty) || 0), 0);
  }, [productKey]);

  const stockInfo = useMemo(() => {
    const t = Number(totalStock) || 0;

    if (t <= 0) {
      return {
        text: lang === "EN" ? "OUT OF STOCK" : "غير متوفر",
        cls: "text-[#7A7674]",
      };
    }
    if (t <= 3) {
      return {
        text: lang === "EN" ? `ONLY ${t} LEFT` : `باقي ${t} فقط`,
        cls: "text-[#C25B2A]",
      };
    }
    return {
      text: lang === "EN" ? "IN STOCK" : "متوفر",
      cls: "text-green-600",
    };
  }, [totalStock, lang]);

  // ✅ UI / SEO derived values (MUST be before useSeo)

const displayName = product
  ? lang === "EN"
    ? product.name_en
    : product.name_ar
  : "";

  const breadcrumbSchema = product
  ? {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: lang === "EN" ? "Home" : "الرئيسية",
          item: window.location.origin,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: lang === "EN" ? "Products" : "المنتجات",
          item: `${window.location.origin}/all-products`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: displayName,
          item: window.location.href,
        },
      ],
    }
  : null;


const description = product
  ? lang === "EN"
    ? product.description_en || product.short_desc_en || ""
    : product.description_ar || product.short_desc_ar || ""
  : "";


const discount = Number(product?.sale_percent) || 0;
const hasDiscount = discount > 0;

const finalPrice = product
  ? hasDiscount
    ? Math.round(product.base_price * (1 - discount / 100))
    : product.base_price
  : 0;




  /* ---------------- TEXT ---------------- */

  const TEXT = {
    EN: {
      addToBag: "ADD TO CART",
similarProductReviews: "Reviews from similar products",
fromProduct: "From",
showMore: "Show more",
showLess: "Show less",
viewAllCustomerReviews: "View all customer reviews",
reviews: "Customer Reviews",
noReviews: "No reviews yet.",
verifiedBuyer: "Verified buyer",
basedOnReviews: "Based on",
review: "review",
reviewsCount: "reviews",
      selectVariant: "Please select size and color.",
      description: "Description",
      delivery: "Delivery & Returns",
      deliveryText:
        "Delivery across Lebanon. Free over $50. Cash on delivery available.",
      returnsText:
        "You can exchange sizes within 3 days if the product is unworn and in original packaging.",
      color: "Color",
      size: "Size",
      material: "Material",
heelHeight: "Heel Height",
shipping: "Shipping & Delivery",
pickupInfo: "Pick up from store",
deliveryInfo: "Delivery fees",
freeDeliveryInfo: "Free delivery on orders above",
cm: "cm",

    },
    AR: {
      addToBag: "أضيفي إلى السلة",
      selectVariant: "يرجى اختيار اللون والمقاس.",
showMore: "عرض المزيد",
showLess: "عرض أقل",
viewAllCustomerReviews: "عرض كل مراجعات العملاء",
      description: "الوصف",
      delivery: "التوصيل والاستبدال",
      deliveryText:
        "توصيل داخل لبنان. توصيل مجاني للطلبات فوق 50$. الدفع عند الاستلام متوفر.",
      returnsText:
        "يمكنك تغيير المقاس خلال 3 أيام في حال لم يتم ارتداء المنتج وكان في علبته الأصلية.",
      color: "اللون",
      size: "المقاس",
similarProductReviews: "مراجعات من منتجات مشابهة",
fromProduct: "من منتج",
reviews: "مراجعات العملاء",
noReviews: "لا توجد مراجعات بعد.",
verifiedBuyer: "عميل موثّق",
basedOnReviews: "بناءً على",
review: "مراجعة",
reviewsCount: "مراجعات",
      material: "الخامة",
heelHeight: "ارتفاع الكعب",
shipping: "التوصيل والاستلام",
pickupInfo: "استلام من المحل",
deliveryInfo: "رسوم التوصيل",
freeDeliveryInfo: "توصيل مجاني للطلبات فوق",
cm: "سم",

    },
  }[lang];

 useSeo(
  "product",
  product?.id,
  product
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: displayName,
        image: product.images?.map((i) => i.url),
        description,
        sku: product.public_id,
        brand: {
          "@type": "Brand",
          name: "ESIELL",
        },
        offers: {
          "@type": "Offer",
          priceCurrency: "USD",
          price: finalPrice,
          availability:
            totalStock > 0
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
          url: window.location.href,
        },

        ...(reviewsData.totalReviews > 0
          ? {
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: Number(reviewsData.averageRating || 0).toFixed(1),
                reviewCount: reviewsData.totalReviews,
              },
              review: reviewsData.reviews.slice(0, 5).map((r) => ({
                "@type": "Review",
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: r.rating,
                  bestRating: 5,
                  worstRating: 1,
                },
                author: {
                  "@type": "Person",
                  name: r.customerName || "Customer",
                },
                reviewBody: r.reviewText || "",
                datePublished: r.createdAt
                  ? new Date(r.createdAt).toISOString().slice(0, 10)
                  : undefined,
              })),
            }
          : {}),

        breadcrumb: breadcrumbSchema,
      }
    : null
);

  useEffect(() => {
  if (!product?.id) return;

  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "view_product",
      entity_type: "product",
      entity_id: product.id,
    }),
  }).catch(() => {});
}, [product?.id]);

useEffect(() => {
  if (!product?.public_id || typeof window.fbq !== "function") return;

  window.fbq("track", "ViewContent", {
    content_ids: [product.public_id],
    content_type: "product",
    value: finalPrice,
    currency: "USD",
  });
}, [product?.public_id, finalPrice]);



const colorToImageIndex = useMemo(() => {
  if (!product?.images) return {};

  const map = {};

  product.images.forEach((img, index) => {
    if (!img.color_name) return;

    const key = normalizeColor(img.color_name);

    // if first time OR lower sort_order → replace
    if (
      map[key] === undefined ||
      Number(img.sort_order) <
        Number(product.images[map[key]]?.sort_order ?? Infinity)
    ) {
      map[key] = index;
    }
  });

  return map;
}, [productKey]);

const scrollToColorImage = (colorName) => {
  const index = colorToImageIndex[normalizeColor(colorName)];
  if (index !== undefined && index !== activeIndex) {
    setActiveIndex(index);
  }
};


const media = useMemo(() => {
  return product?.images || [];
}, [productKey]);

useEffect(() => {
  if (!media.length) return;

  media.forEach((m) => {
    if (!m.url) return;

    const img = new Image();
    img.src = m.url;
  });
}, [media]);


const activeMedia = media[activeIndex];
const isVideo = /\.(mp4|webm|ogg)$/i.test(activeMedia?.url);


const prevMedia = () => {
  setActiveIndex((i) => (i === 0 ? media.length - 1 : i - 1));
};

const nextMedia = () => {
  setActiveIndex((i) => (i === media.length - 1 ? 0 : i + 1));
};

useEffect(() => {
  if (!product || !normalizedColorFromUrl || selectedColor) return;


  // find matching color name from product variants
  const match = (product.variants || []).find(
    (v) => normalizeColor(v.color_name) === normalizedColorFromUrl
  );

  if (!match) return;

  // ✅ set selected color
  setSelectedColor(match.color_name);
  setSelectedVariant(null);
  setError("");

  // ✅ switch image to first image of that color
  scrollToColorImage(match.color_name);
}, [productKey, normalizedColorFromUrl]);


  /* ---------------- EARLY RETURNS ---------------- */

  if (loading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center">
        <p className="text-sm text-[#7A7674]">Loading product…</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="w-full min-h-screen pt-[95px] px-4 pb-24">
        <button
            onClick={() => navigate(-1)}
            className="
              absolute top-[110px]
              text-3xl text-[#2E3135]
              opacity-60 hover:opacity-100
            "
            style={lang === "AR" ? { right: "16px" } : { left: "16px" }}
          >
            ×
          </button>

        <p className="mt-20 text-center">Product not found.</p>
      </div>
    );
  }

  /* ---------------- UI DATA ---------------- */

    const whatsappMessage = `Hi, I need help with: ${displayName} (${product?.public_id || ""})`;
  const whatsappLink = `https://wa.me/96179338100?text=${encodeURIComponent(whatsappMessage)}`;


   const material =
  lang === "EN" ? product.material_en : product.material_ar;

const heel = product.heel_height;



  const inWishlist = isInWishlist(product.id);

  /* ---------------- ACTIONS ---------------- */

  const handleAddToBag = () => {
  if (!selectedColor || !selectedVariant) {
    setError(TEXT.selectVariant);
    return;
  }

  setError("");
  setIsPlacing(true);

  if (mainImageRef.current) {
    flyImageToCart(mainImageRef.current);
  }

  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "add_to_cart",
      entity_type: "product",
      entity_id: product.id,
    }),
  }).catch(() => {});

  // ✅ ADD THIS
  if (typeof window.fbq === "function") {
    window.fbq("track", "AddToCart", {
      content_ids: [product.public_id],
      content_type: "product",
      value: finalPrice,
      currency: "USD",
    });
  }

  addToCart({
    product_id: product.id,
    variant_id: selectedVariant.id,
    public_id: product.public_id,
    nameEN: product.name_en,
    nameAR: product.name_ar,
    price: finalPrice + (selectedVariant.extra_price || 0),
    image: product.images?.[0]?.url,
    color: selectedVariant.color_name,
    color_hex: selectedVariant.color_hex || "#000000",
    size: selectedVariant.size,
  });

  setTimeout(() => setIsPlacing(false), 800);
};


const renderStars = (rating) => {
  const safeRating = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));

  return (
    <span className="text-yellow-500 tracking-[1px]">
      {"★".repeat(safeRating)}
      <span className="text-[#D8CDC8]">{"★".repeat(5 - safeRating)}</span>
    </span>
  );
};

const formatReviewDate = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleDateString(lang === "AR" ? "ar" : "en", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getReviewMediaItems = (review) => {
  if (
    Array.isArray(review.reviewMediaItems) &&
    review.reviewMediaItems.length > 0
  ) {
    return review.reviewMediaItems.filter(
      (item) => item?.url && item?.type
    );
  }

  if (review.reviewMediaUrl && review.reviewMediaType) {
    return [
      {
        id: `legacy-${review.id}`,
        type: review.reviewMediaType,
        url: review.reviewMediaUrl,
        mimeType: review.reviewMediaMimeType || null,
        filename: review.reviewMediaFilename || null,
      },
    ];
  }

  return [];
};

const renderReviewMediaItem = (mediaItem, reviewId, index) => {
  const key = mediaItem.id || `${reviewId}-${index}`;

  if (mediaItem.type === "image") {
    return (
      <button
        key={key}
        type="button"
        onClick={() =>
          window.open(
            mediaItem.url,
            "_blank",
            "noopener,noreferrer"
          )
        }
        className="block w-full overflow-hidden rounded-xl border border-[#E6D8D2] bg-[#FFF9F6]"
      >
        <img
          src={mediaItem.url}
          alt={`Customer review ${index + 1}`}
          loading="lazy"
          className="h-56 sm:h-72 w-full object-contain"
        />
      </button>
    );
  }

  if (mediaItem.type === "video") {
    return (
      <div
        key={key}
        className="overflow-hidden rounded-xl border border-[#E6D8D2] bg-black"
      >
        <video
          controls
          playsInline
          preload="metadata"
          className="h-56 sm:h-72 w-full object-contain"
        >
          <source
            src={mediaItem.url}
            type={mediaItem.mimeType || "video/mp4"}
          />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  if (mediaItem.type === "audio") {
    return (
      <div
        key={key}
        className="rounded-xl border border-[#E6D8D2] bg-[#FFF9F6] p-3"
      >
        <p className="mb-2 text-xs text-[#7A7674]">
          {lang === "EN"
            ? `Voice review ${index + 1}`
            : `مراجعة صوتية ${index + 1}`}
        </p>

        <audio controls preload="metadata" className="w-full">
          <source
            src={mediaItem.url}
            type={mediaItem.mimeType || "audio/ogg"}
          />
          Your browser does not support the audio tag.
        </audio>
      </div>
    );
  }

  return (
    <a
      key={key}
      href={mediaItem.url}
      target="_blank"
      rel="noreferrer"
      className="block rounded-xl border border-[#E6D8D2] bg-[#FFF9F6] p-3 text-sm font-semibold text-black underline"
    >
      {mediaItem.filename ||
        (lang === "EN" ? "Open review media" : "عرض وسائط المراجعة")}
    </a>
  );
};

const renderReviewMediaGallery = (review) => {
  const mediaItems = getReviewMediaItems(review);

  if (mediaItems.length === 0) return null;

  return (
    <div
      className={`mt-4 grid gap-3 ${
        mediaItems.length === 1
          ? "grid-cols-1"
          : "grid-cols-1 sm:grid-cols-2"
      }`}
    >
      {mediaItems.map((mediaItem, index) =>
        renderReviewMediaItem(mediaItem, review.id, index)
      )}
    </div>
  );
};

const hasReviewsToDisplay = reviewsData.reviews.length > 0;

const visibleReviews = reviewsExpanded
  ? reviewsData.reviews
  : reviewsData.reviews.slice(0, 3);

  /* ---------------- RENDER ---------------- */

  return (
    <div className="w-full bg-white min-h-screen flex flex-col overflow-x-hidden">
      <TopBanner />
      <MainHeader />
      <CartDrawer />
      <AddedToBagToast />

      <div
        className="w-full min-h-screen bg-white pt-[95px] px-4 pb-24"
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
        <button
  onClick={() => navigate(-1)}
  className="
    absolute top-[110px]
    text-3xl text-[#2E3135]
    opacity-60 hover:opacity-100
  "
  style={lang === "AR" ? { right: "16px" } : { left: "16px" }}
>
  ×
</button>


        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6 mt-2">
          {/* IMAGE */}
          <div>
            <div className="relative">
             {hasDiscount && (
  <span className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1 rounded-full z-10">
    -{discount}%
  </span>
)}


<div className="relative">
  {isVideo ? (
  <video
    key={activeMedia.url}          // ✅ FORCE REMOUNT
    ref={mainImageRef}
    controls
    muted                           // ✅ REQUIRED FOR MOBILE
    playsInline
    preload="metadata"
    className="w-full max-h-[520px] object-contain border rounded-lg"
  >
    <source src={activeMedia.url} type="video/mp4" />
    Your browser does not support the video tag.
  </video>
) : (
  <img
    ref={mainImageRef}
    src={activeMedia.url}
    loading="eager"
  decoding="async"
    alt={displayName}
    className="w-full max-h-[520px] object-contain border rounded-lg"
  />
)}

  {/* PREV */}
  {media.length > 1 && (
    <>
      <button
        onClick={lang === "AR" ? nextMedia : prevMedia}
        className="
          absolute left-0 top-1/2 -translate-y-1/2
          w-8 h-8
          bg-black/40 hover:bg-black/20
          text-white text-sm font-bold
          flex items-center justify-center
          rounded
        "
      >
        {lang === "AR" ? "›" : "‹"}
      </button>

      {/* NEXT */}
      <button
        onClick={lang === "AR" ? prevMedia : nextMedia}
        className="
          absolute right-0 top-1/2 -translate-y-1/2
          w-8 h-8
          bg-black/40 hover:bg-black/20
          text-white text-sm font-bold
          flex items-center justify-center
          rounded
        "
      >
        {lang === "AR" ? "‹" : "›"}
      </button>
    </>
  )}

  {/* DOTS */}
  {media.length > 1 && (
    <div
  className={`
    absolute bottom-3 flex gap-2
    ${lang === "AR" ? "left-3" : "right-3"}
  `}
>
      {media.map((_, i) => (
        <button
          key={i}
          onClick={() => setActiveIndex(i)}
          className={`
            w-2.5 h-2.5 rounded-full border
            ${i === activeIndex ? "bg-white border-white" : "border-white"}
          `}
        />
      ))}
    </div>
  )}
</div>


            </div>
          </div>

          {/* INFO */}
          <div>
            <h1 className="text-xl font-semibold">{displayName}</h1>

{product.public_id && (
  <p className="text-[12px] text-[#9F9087] mt-1">
    {product.public_id}
  </p>
)}

<div className="mt-2 flex items-center gap-2">
  <span className="text-lg font-bold text-black">${finalPrice}</span>

  {hasDiscount && (
    <span className="text-sm line-through text-[#9F9087]">
      ${product.base_price}
    </span>
  )}
</div>

{/* ✅ NEW STOCK STATUS */}
<div className={`mt-2 text-sm font-semibold ${stockInfo.cls}`}>
  {stockInfo.text}
</div>

{hasReviewsToDisplay && (
  <div className="mt-3 flex items-center gap-2 text-sm">
    <span>{renderStars(reviewsData.averageRating)}</span>
    <span className="text-[#7A7674]">
      {reviewsData.averageRating.toFixed(1)} • {reviewsData.totalReviews}{" "}
      {reviewsData.totalReviews === 1 ? TEXT.review : TEXT.reviewsCount}
    </span>
  </div>
)}


            {/* COLOR */}
            <div className="mt-5">
              <p className="text-xs font-semibold mb-2">{TEXT.color}</p>
              <div className="flex gap-2">
                {colors.map((c) => (
                  <button
                    key={c.name}
  onClick={() => {
  if (selectedColor === c.name) return;

  setSelectedColor(c.name);
  setSelectedVariant(null);
  setError("");

  // ✅ update URL
  setColorInUrl(c.name);

  // ✅ switch image instantly
  scrollToColorImage(c.name);
}}

                    className={`w-8 h-8 rounded-full border transition
                              ${
                                selectedColor === c.name
                                  ? "border-black ring-2 ring-black/30"
                                  : "border-[#D3C5BE]"
                              }
                            `}
                    style={{ backgroundColor: c.code }}
                  />
                ))}
              </div>
            </div>

            {/* SIZE */}
            {sizesByColor.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold mb-2">{TEXT.size}</p>
                <div className="flex gap-2 flex-wrap">
                  {sizesByColor.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => {
                        setSelectedVariant(v);
                        setError("");
                      }}
                      className={`px-3 py-2 text-xs rounded-full border transition
  ${
    selectedVariant?.id === v.id
      ? "bg-black text-white border-black"
      : "border-[#D3C5BE]"
  }
`}

                    >
                      {v.size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && <p className="mt-2 text-xs text-red-500">{error}</p>}

            <button
              onClick={handleAddToBag}
              disabled={isPlacing}
              className="mt-6 w-full py-3 bg-black text-white rounded-md font-semibold"
            >
              {TEXT.addToBag}
            </button>

            {/* DETAILS DROPDOWNS (Footer style) */}
<div className="mt-6 border border-[#E6D8D2] rounded-lg overflow-hidden">
  {/* 1) Description */}
  <div
    className="px-4 py-3 border-b border-[#E6D8D2] flex justify-between items-center cursor-pointer"
    onClick={() => setOpenDesc((v) => !v)}
  >
    <div className="flex items-center gap-2 text-sm font-semibold text-[#2E3135]">
      <FaAlignLeft className="text-[#7A7674]" />
      <span>{TEXT.description}</span>
    </div>
    <span className="text-xl text-[#2E3135]">{openDesc ? "−" : "+"}</span>
  </div>

  <div
    className={`
      px-4 text-xs leading-5 text-[#5c5150] transition-all duration-300
      ${openDesc ? "max-h-40 opacity-100 py-3" : "max-h-0 opacity-0 py-0"}
      overflow-hidden
    `}
  >
    <p>{description || "—"}</p>
  </div>

  {/* 2) Material */}
  <div
    className="px-4 py-3 border-t border-[#E6D8D2] flex justify-between items-center cursor-pointer"
    onClick={() => setOpenMaterial((v) => !v)}
  >
    <div className="flex items-center gap-2 text-sm font-semibold text-[#2E3135]">
      <FaCubes className="text-[#7A7674]" />
      <span>{TEXT.material}</span>
    </div>
    <span className="text-xl text-[#2E3135]">{openMaterial ? "−" : "+"}</span>
  </div>

  <div
    className={`
      px-4 text-xs leading-5 text-[#5c5150] transition-all duration-300
      ${openMaterial ? "max-h-24 opacity-100 py-3" : "max-h-0 opacity-0 py-0"}
      overflow-hidden
    `}
  >
    <p>{material || "—"}</p>
  </div>

  {/* 3) Heel Height (ONLY if > 0) */}
{Number(heel) > 0 && (
  <>
    <div
      className="px-4 py-3 border-t border-[#E6D8D2] flex justify-between items-center cursor-pointer"
      onClick={() => setOpenHeel((v) => !v)}
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-[#2E3135]">
        <FaRulerVertical className="text-[#7A7674]" />
        <span>{TEXT.heelHeight}</span>
      </div>
      <span className="text-xl text-[#2E3135]">{openHeel ? "−" : "+"}</span>
    </div>

    <div
      className={`
        px-4 text-xs leading-5 text-[#5c5150] transition-all duration-300
        ${openHeel ? "max-h-24 opacity-100 py-3" : "max-h-0 opacity-0 py-0"}
        overflow-hidden
      `}
    >
      <p>{Number(heel)} {TEXT.cm}</p>
    </div>
  </>
)}


  {/* 4) Shipping & Delivery */}
  <div
    className="px-4 py-3 border-t border-[#E6D8D2] flex justify-between items-center cursor-pointer"
    onClick={() => setOpenShip((v) => !v)}
  >
    <div className="flex items-center gap-2 text-sm font-semibold text-[#2E3135]">
      <FaTruck className="text-[#7A7674]" />
      <span>{TEXT.shipping}</span>
    </div>
    <span className="text-xl text-[#2E3135]">{openShip ? "−" : "+"}</span>
  </div>

  <div
    className={`
      px-4 text-xs leading-5 text-[#5c5150] transition-all duration-300
      ${openShip ? "max-h-40 opacity-100 py-3" : "max-h-0 opacity-0 py-0"}
      overflow-hidden
    `}
  >
    <p>
      • {TEXT.deliveryInfo}: ${Number(shipping.fee || 0).toFixed(2)}
    </p>

    {shipping.free_enabled && Number(shipping.free_threshold) > 0 && (
      <p className="mt-1">
        • {TEXT.freeDeliveryInfo}: ${Number(shipping.free_threshold).toFixed(2)}
      </p>
    )}

    <p className="mt-1">• {TEXT.pickupInfo}: $0.00</p>
  </div>
</div>

          </div>
        </div>

  {/* PRODUCT REVIEWS */}
        {hasReviewsToDisplay && (
        <div className="max-w-5xl mx-auto mt-10">
          <div className="border border-[#E6D8D2] rounded-2xl bg-[#FFF9F6] overflow-hidden">
            <div className="p-5 border-b border-[#E6D8D2] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[#2E3135]">
  {reviewsData.isFallback ? TEXT.similarProductReviews : TEXT.reviews}
</h2>

                <p className="text-xs text-[#7A7674] mt-1">
  {reviewsData.isFallback
    ? lang === "EN"
      ? "This product has no reviews yet, so we are showing reviews from similar products."
      : "هذا المنتج لا يحتوي على مراجعات بعد، لذلك نعرض مراجعات من منتجات مشابهة."
    : `${TEXT.basedOnReviews} ${reviewsData.totalReviews} ${
        reviewsData.totalReviews === 1 ? TEXT.review : TEXT.reviewsCount
      }`}
</p>
              </div>

{reviewsData.totalReviews > 0 && !reviewsData.isFallback && (
  <div className="flex items-center gap-3">
                  <div className="text-2xl font-bold text-[#2E3135]">
                    {reviewsData.averageRating.toFixed(1)}
                  </div>
                  <div className="text-sm">
                    {renderStars(reviewsData.averageRating)}
                  </div>
                </div>
              )}
            </div>

            <div className="p-5">
              {reviewsLoading ? (
                <div className="text-sm text-[#7A7674]">
                  {lang === "EN" ? "Loading reviews..." : "جاري تحميل المراجعات..."}
                </div>
              ) : reviewsData.reviews.length === 0 ? (
                <div className="text-sm text-[#7A7674]">
                  {TEXT.noReviews}
                </div>
              ) : (
                <div className="grid gap-4">
                  {visibleReviews.map((review) => (
                    <div
                      key={review.id}
                      className="bg-white border border-[#E6D8D2] rounded-2xl p-4 shadow-sm"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center text-sm font-semibold">
                              {(review.customerName || "C")
                                .trim()
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <div>
                              <div className="text-sm font-semibold text-[#2E3135]">
                                {review.customerName}
                              </div>
                              <div className="text-[11px] text-green-700 font-medium">
                                ✓ {TEXT.verifiedBuyer}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div
                          className={`text-xs text-[#9F9087] ${
                            lang === "AR" ? "sm:text-left" : "sm:text-right"
                          }`}
                        >
                          <div>{renderStars(review.rating)}</div>
              <div className="mt-1">
  {formatReviewDate(review.createdAt)}
</div>
{review.isFallback && review.seoSlug && (
  <button
    type="button"
    onClick={() => navigate(`/product/${review.seoSlug}`)}
    className="mt-3 flex items-center gap-3 rounded-xl border border-[#E6D8D2] bg-[#FFF9F6] p-2 hover:bg-white transition"
  >
    <div className="w-14 h-14 rounded-lg border border-[#E6D8D2] overflow-hidden bg-white flex-shrink-0">
      <img
        src={review.productImage || "/images/placeholder-product.jpg"}
        alt={
          lang === "AR"
            ? review.productNameAr || review.productNameEn
            : review.productNameEn || review.productNameAr
        }
        className="w-full h-full object-cover"
      />
    </div>

    <div className={lang === "AR" ? "text-right" : "text-left"}>
      <div className="text-[11px] text-[#9F9087]">
        {TEXT.fromProduct}
      </div>
      <div className="text-xs font-semibold text-[#2E3135] line-clamp-2">
        {lang === "AR"
          ? review.productNameAr || review.productNameEn
          : review.productNameEn || review.productNameAr}
      </div>
    </div>
  </button>
)}
                        </div>
                      </div>

                                            {review.reviewText && (
                        <p className="mt-4 text-sm leading-6 text-[#5c5150] whitespace-pre-line">
                          {review.reviewText}
                        </p>
                      )}

                      {renderReviewMediaGallery(review)}
                    </div>
                  ))}
                              </div>
              )}

              <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
                {reviewsData.reviews.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setReviewsExpanded((v) => !v)}
                    className="px-5 py-2.5 rounded-lg text-sm font-semibold border border-[#E6D8D2] bg-white text-[#2E3135] hover:bg-[#FFF9F6] transition"
                  >
                    {reviewsExpanded ? TEXT.showLess : TEXT.showMore}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => navigate("/customer-reviews")}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-black text-white hover:bg-[#222] transition"
                >
                  {TEXT.viewAllCustomerReviews}
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

                {/* COMPLETE YOUR OUTFIT */}
        {outfitProducts.length > 0 && (
          <div className="max-w-5xl mx-auto mt-10">
            <h2 className="text-lg font-semibold text-[#2E3135] mb-4">
              {lang === "EN" ? "Complete your outfit" : "أكملي إطلالتك"}
            </h2>

            <div className="flex gap-3 overflow-x-auto pb-2">
              {outfitProducts.map((p) => (
                <div key={p.id} className="min-w-[220px] max-w-[220px]">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        )}

       {/* RELATED PRODUCTS */}
{relatedProducts.length > 0 && (
  <div className="max-w-5xl mx-auto mt-12">
    <h2 className="text-lg font-semibold text-[#2E3135] mb-4">
      {lang === "EN" ? "Related products" : "منتجات مشابهة"}
    </h2>

    <div className="flex gap-3 overflow-x-auto pb-2">
      {relatedProducts.map((p) => (
        <div key={p.id} className="min-w-[220px] max-w-[220px]">
          <ProductCard product={p} />
        </div>
      ))}
    </div>
  </div>
)}

      {/* EXPLORE ALL PRODUCTS BUTTON */}
<div className="max-w-5xl mx-auto mt-14 flex justify-center">
  <button
    onClick={() => navigate("/all-products")}
    className="
      w-full max-w-md
      py-3
      bg-black text-white
      rounded-lg
      text-sm font-semibold tracking-wide
      shadow-[0_4px_12px_rgba(0,0,0,0.25)]
      active:scale-[0.98]
      transition-all
    "
  >
    {lang === "EN" ? "EXPLORE ALL PRODUCTS" : "تصفح كل المنتجات"}
  </button>
</div>


      </div>
      {/* ✅ STICKY WHATSAPP CTA */}
{showWhatsappCTA && (
  <div
    className="fixed z-[9999] bottom-20 right-5 flex flex-col items-center gap-2"
    dir="ltr"
  >
    {/* Close button ABOVE */}
    <button
      onClick={() => setShowWhatsappCTA(false)}
      className="w-7 h-7 rounded-full bg-white border border-[#E6D8D2] flex items-center justify-center text-black text-sm shadow-md opacity-90 hover:opacity-100"
      aria-label="Close WhatsApp"
      title="Close"
    >
      ×
    </button>

    {/* WhatsApp Button */}
    <a
  href={whatsappLink}
  target="_blank"
  rel="noreferrer"
  className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition"
  style={{ backgroundColor: "#25D366" }}
  aria-label="WhatsApp"
  title="WhatsApp"
>
  <img
    src="/icons/whatsapp.png"
    alt="WhatsApp"
    className="w-7 h-7 object-contain"
  />
</a>

  </div>
)}

    {/* ⬆️ SCROLL TO TOP BUTTON */}
{showScrollTop && (
  <button
    onClick={() =>
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
    className="
  fixed
  z-[9999]
  top-24
  left-1/2
  -translate-x-1/2
  w-12 h-12
  rounded-full
  bg-black
  text-white
  text-xl
  flex items-center justify-center
  shadow-lg
  opacity-90
  hover:opacity-100
  active:scale-95
  transition
"
    aria-label="Scroll to top"
    title="Scroll to top"
  >
    ↑
  </button>
)}

{viewerOpen && (
  <div
    className="
      fixed inset-0 z-[10000]
      bg-black/95
      flex items-center justify-center
    "
    onClick={() => setViewerOpen(false)}
  >
    {/* CLOSE */}
    <button
      onClick={() => setViewerOpen(false)}
      className="
        absolute top-4 right-4
        text-white text-3xl
        opacity-80 hover:opacity-100
      "
    >
      ×
    </button>

    {/* HORIZONTAL SCROLLER */}
    <div
      className="
        w-full h-full
        flex overflow-x-auto
        snap-x snap-mandatory
        scrollbar-hide
      "
      onClick={(e) => e.stopPropagation()}
    >
       {media.map((m, i) => {
  const isVid = /\.(mp4|webm|ogg)$/i.test(m.url);

  return (
    <div key={i} className="min-w-full h-full flex items-center justify-center snap-center">
      {isVid ? (
        <video
  key={m.url}
  controls
  muted
  playsInline
  preload="metadata"
  className="max-w-full max-h-full object-contain"
>
  <source src={m.url} type="video/mp4" />
</video>
      ) : (
        <img src={m.url} className="max-w-full max-h-full object-contain" />
      )}
    </div>
  );
})}

    </div>
  </div>
)}
<Footer className="mt-auto" />
    </div>
  );
}

export default ProductDetailsPage;


